<template>
  <div class="page-outer storefront-pages-list-page">
    <div class="page-header page-header-storefront-pages">
      <div class="header-left">
        <h1 class="page-title"><i class="ri-file-text-line"></i> 前台頁面管理</h1>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="hasAboutPage || seeding"
          :title="hasAboutPage ? '已存在 slug 為 about 的頁面' : ''"
          @click="seedAboutUs"
        >
          <span v-if="seeding" class="spinner-sm"></span>
          <i v-else class="ri-magic-line"></i>
          {{ seeding ? '建立中…' : '建立「關於我們」範本' }}
        </button>
        <button type="button" class="btn btn-primary" @click="goToCreatePage">
          <i class="ri-add-line"></i> 新增頁面
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>
      <div v-else-if="error" class="error-state">{{ error }}</div>
      <div v-else>
      <div v-if="!pages.length" class="empty-state">
        <i class="ri-file-text-line empty-icon"></i>
        <p>尚無頁面。可一鍵建立「關於我們」範本（Hero + 文字區塊，slug：<code>about</code>），或自行新增 CMS 單元頁。</p>
        <div class="empty-actions">
          <button type="button" class="btn btn-secondary" :disabled="seeding" @click="seedAboutUs">
            <span v-if="seeding" class="spinner-sm"></span>
            <i v-else class="ri-magic-line"></i>
            {{ seeding ? '建立中…' : '建立「關於我們」範本' }}
          </button>
          <button type="button" class="btn btn-primary" @click="goToCreatePage">
            <i class="ri-add-line"></i> 新增頁面
          </button>
        </div>
      </div>
      <div v-else class="table-wrapper">
        <table class="table">
          <thead>
            <tr>
              <th>標題</th>
              <th>Slug（URL）</th>
              <th>類型</th>
              <th>狀態</th>
              <th>排序</th>
              <th>最後更新</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="page in pages" :key="page.id">
              <td class="page-title-col">{{ page.title }}</td>
              <td class="page-slug">{{ page.slug }}</td>
              <td>
                <span class="badge" :class="page.page_type === 'system' ? 'badge--system' : 'badge--custom'">
                  {{ page.page_type === 'system' ? '系統' : '自訂' }}
                </span>
              </td>
              <td>
                <span class="status-badge" :class="page.status">{{ STATUS_LABELS[page.status] ?? page.status }}</span>
              </td>
              <td>{{ page.sort_order }}</td>
              <td class="text-muted">{{ formatDate(page.updated_at) }}</td>
              <td>
                <div class="action-btns">
                  <button class="btn btn-secondary btn-sm" @click="goToEditPage(page)"><i class="ri-edit-line"></i> 編輯</button>
                  <button
                    class="btn btn-danger btn-sm"
                    :disabled="page.page_type === 'system'"
                    :title="page.page_type === 'system' ? '系統頁面不可刪除' : '刪除'"
                    @click="confirmDelete(page)"
                  ><i class="ri-delete-bin-line"></i></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { showError, showSuccess, showDeleteConfirm } from '@/utils/swal.js'
import { buildDefaultAboutUsPageBody } from '@/constants/defaultStorefrontAboutPage.js'

usePageTitle('前台頁面管理')

const router = useRouter()

const STATUS_LABELS = { active: '上架', draft: '草稿', inactive: '下架' }

const pages = ref([])
const loading = ref(false)
const error = ref('')
const seeding = ref(false)

const hasAboutPage = computed(() =>
  pages.value.some((p) => String(p.slug || '').toLowerCase() === 'about')
)

function formatDate(d) { return new Date(d).toLocaleDateString('zh-TW') }

async function loadPages() {
  loading.value = true
  error.value = ''
  try {
    const res = await commerceSettingsApi.listPages()
    pages.value = res?.data?.items ?? []
  } catch (e) {
    error.value = e?.response?.data?.message || '載入失敗'
  } finally {
    loading.value = false
  }
}

function goToCreatePage() {
  router.push('/client/commerce/pages/create')
}

function goToEditPage(page) {
  router.push(`/client/commerce/pages/${encodeURIComponent(page.slug)}/edit`)
}

async function confirmDelete(page) {
  if (page.page_type === 'system') return
  const confirmed = await showDeleteConfirm(`確定刪除「${page.title}」？`)
  if (!confirmed) return
  try {
    await commerceSettingsApi.deletePage(page.slug)
    await showSuccess('頁面已刪除')
    await loadPages()
  } catch (e) {
    await showError(e?.response?.data?.message || '刪除失敗')
  }
}

async function seedAboutUs() {
  if (hasAboutPage.value) {
    await showError('已存在 slug 為 about 的頁面，請直接編輯或改用其他 slug。')
    return
  }
  seeding.value = true
  try {
    await commerceSettingsApi.createPage(buildDefaultAboutUsPageBody())
    await showSuccess('已建立「關於我們」範本（slug：about）')
    await loadPages()
  } catch (e) {
    await showError(e?.response?.data?.message || '建立失敗')
  } finally {
    seeding.value = false
  }
}

onMounted(loadPages)
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  margin-bottom: 0;
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-actions { display: flex; align-items: center; gap: 8px; }

.page-header .page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-secondary { background: #fff; color: #374151; border: 1px solid #d1d5db; }
.btn-secondary:hover:not(:disabled) { background: #f9fafb; border-color: #9ca3af; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: sf-spin 0.7s linear infinite;
}

@keyframes sf-spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #6b7280;
}

.empty-state .empty-icon {
  font-size: 3rem;
  color: #d1d5db;
  display: block;
  margin-bottom: 0.75rem;
}

.empty-state code {
  font-size: 0.85em;
  background: #f3f4f6;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
}

.empty-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 1.25rem;
}

.page-title-col { font-weight: 500; }
.page-slug { font-size: 12px; color: #6b7280; }
.text-muted { font-size: 12px; color: #9ca3af; }

.badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
}
.badge--system { background: #e0e7ff; color: #4338ca; }
.badge--custom { background: #f0fdf4; color: #166534; }

.status-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
}
.status-badge.active { background: #d1fae5; color: #065f46; }
.status-badge.draft { background: #fef9c3; color: #854d0e; }
.status-badge.inactive { background: #f3f4f6; color: #6b7280; }

.action-btns { display: flex; gap: 4px; }

.table-wrapper table { min-width: 700px; }
</style>

<style>
.page-header-storefront-pages {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-storefront-pages {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-storefront-pages {
    left: 0 !important;
    top: 56px;
  }
}
</style>

